import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Loader2, 
  ArrowRight,
  RefreshCw,
  LogIn,
  Mail
} from 'lucide-react';
import { useRegistrationFlow } from '../contexts/RegistrationFlowContext';
import { useAuth } from '../hooks/auth';

const REDIRECT_DELAY = 2000;

export function VerifyEmailHandler() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  
  const {
    step,
    error,
    isLoading,
    handleVerificationLink,
    resendVerificationEmail,
    checkVerificationStatus,
    resetFlow
  } = useRegistrationFlow();

  const [countdown, setCountdown] = useState(REDIRECT_DELAY / 1000);
  const [hasAttempted, setHasAttempted] = useState(false);

  const oobCode = searchParams.get('oobCode');

  // Handle verification on mount
  useEffect(() => {
    if (hasAttempted || !oobCode) return;
    
    console.log('[VerifyEmailHandler] Starting verification with oobCode:', oobCode.substring(0, 10) + '...');
    setHasAttempted(true);
    
    handleVerificationLink(oobCode);
  }, [oobCode, hasAttempted, handleVerificationLink]);

  // Handle countdown for redirect
  useEffect(() => {
    if (step === 'verified' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 'verified' && countdown === 0) {
      console.log('[VerifyEmailHandler] Countdown complete, redirecting to role-selection');
      navigate('/role-selection', { replace: true });
    }
  }, [step, countdown, navigate]);

  // Handle missing oobCode
  if (!oobCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6366f1]/20 via-surface to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface/80 backdrop-blur-xl border border-border/50 rounded-3xl p-8 shadow-2xl max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Недійсне посилання
          </h1>
          <p className="text-text-muted mb-6">
            Це посилання не містить необхідного коду для підтвердження.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3.5 bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
          >
            <LogIn className="w-5 h-5" />
            Увійти
          </button>
        </motion.div>
      </div>
    );
  }

  const handleResend = async () => {
    await resendVerificationEmail();
    if (!error) {
      navigate('/verify-email-pending');
    }
  };

  const handleCheckStatus = async () => {
    const isVerified = await checkVerificationStatus();
    if (isVerified) {
      navigate('/role-selection');
    }
  };

  const handleGoToLogin = () => {
    resetFlow();
    navigate('/login');
  };

  const handleRetry = () => {
    setHasAttempted(false);
    setCountdown(REDIRECT_DELAY / 1000);
    if (oobCode) {
      handleVerificationLink(oobCode);
    }
  };

  // Render loading state
  if (isLoading || step === 'verifying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6366f1]/20 via-surface to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 mx-auto mb-6 relative">
            <motion.div
              className="absolute inset-0 border-4 border-[#6366f1]/20 rounded-full"
              style={{ borderTopColor: '#6366f1' }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#6366f1] to-[#4f46e5] rounded-xl flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Перевіряємо email...
          </h1>
          <p className="text-text-muted">
            Зачекайте, будь ласка
          </p>
        </motion.div>
      </div>
    );
  }

  // Render success state
  if (step === 'verified') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6366f1]/20 via-surface to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-surface/80 backdrop-blur-xl border border-border/50 rounded-3xl p-8 shadow-2xl max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-500" />
          </motion.div>
          
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Email підтверджено!
          </h1>
          
          {authUser ? (
            <>
              <p className="text-text-muted mb-6">
                Ваш email успішно верифіковано.
              </p>
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 text-sm text-text-muted mb-2">
                  <Clock className="w-4 h-4" />
                  <span>Перенаправлення через {countdown} сек...</span>
                </div>
                <div className="w-full h-1 bg-elevated rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#6366f1] to-[#4f46e5]"
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: REDIRECT_DELAY / 1000, ease: 'linear' }}
                  />
                </div>
              </div>
              <button
                onClick={() => navigate('/role-selection')}
                className="w-full py-3.5 bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                Продовжити зараз
                <ArrowRight className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <p className="text-text-muted mb-2">
                Ваш email успішно верифіковано.
              </p>
              <p className="text-text-muted/70 text-sm mb-6">
                Увійдіть в систему для продовження.
              </p>
              <button
                onClick={handleGoToLogin}
                className="w-full py-3.5 bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <LogIn className="w-5 h-5" />
                Увійти
              </button>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  // Render error states
  if (step === 'error' && error) {
    const isExpired = error.type === 'expired';

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6366f1]/20 via-surface to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-surface/80 backdrop-blur-xl border border-border/50 rounded-3xl p-8 shadow-2xl max-w-md w-full text-center"
        >
          <div className={`w-20 h-20 ${isExpired ? 'bg-orange-500/20' : 'bg-red-500/20'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {isExpired ? (
              <Clock className="w-10 h-10 text-orange-500" />
            ) : (
              <AlertTriangle className="w-10 h-10 text-red-500" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {isExpired ? 'Посилання застаріло' : 'Помилка підтвердження'}
          </h1>
          
          <p className="text-text-muted mb-2">
            {error.message}
          </p>
          
          {isExpired && (
            <p className="text-text-muted/70 text-sm mb-6">
              Посилання дійсне лише 24 години.
            </p>
          )}

          <div className="space-y-3">
            {error.action === 'resend' && (
              <button
                onClick={handleResend}
                className="w-full py-3.5 bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Надіслати лист повторно
              </button>
            )}
            
            {error.action === 'retry' && (
              <button
                onClick={handleRetry}
                className="w-full py-3.5 bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Спробувати ще раз
              </button>
            )}

            {error.action === 'login' && (
              <button
                onClick={handleCheckStatus}
                className="w-full py-3.5 bg-gradient-to-r from-[#6366f1] to-[#4f46e5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Перевірити статус
              </button>
            )}

            <button
              onClick={handleGoToLogin}
              className="w-full py-3.5 bg-elevated hover:bg-elevated/80 text-text-primary font-medium rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <LogIn className="w-5 h-5" />
              Увійти
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
}
